import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Activity, Shield, Users, Calendar } from "lucide-react";

export default function ClinicBadge() {
  const { clinicId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/badges/${clinicId}`); setData(r.data); }
      catch (e) { setErr(e.response?.data?.detail || "Badge not found"); }
    })();
  }, [clinicId]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-zinc-500" data-testid="badge-error">{err}</div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-zinc-50">Loading…</div>;

  const isPrivacyProtected = data.score === null;

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="border-b border-zinc-200 bg-white">
        <div className="container-x h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--ink)" }}>
              <Activity className="w-5 h-5" style={{ color: "var(--gold)" }} />
            </span>
            <span className="font-extrabold tracking-tight">Sonote<span style={{ color: "var(--gold)" }}>™</span></span>
          </Link>
          <Link to="/features" className="text-sm text-zinc-600 hover:text-zinc-900">What is this?</Link>
        </div>
      </header>

      <main className="container-x py-16 max-w-3xl">
        <span className="pill" data-testid="badge-pill">Clinic Wellness Index</span>
        <h1 className="mt-4 text-4xl sm:text-5xl font-black tracking-tight" data-testid="badge-clinic-name">{data.clinic_name}</h1>
        {data.location && <p className="text-zinc-500 mt-1">{data.location}</p>}

        <section className="mt-10 rounded-3xl border border-zinc-200 overflow-hidden grid md:grid-cols-2" data-testid="badge-card">
          <div className="p-10" style={{ background: data.color, color: "#fff" }}>
            <div className="text-xs uppercase tracking-[0.18em] font-bold opacity-90">{data.month}</div>
            {isPrivacyProtected ? (
              <>
                <div className="mt-4 text-3xl font-extrabold">Privacy-protected</div>
                <p className="mt-3 text-sm opacity-90 max-w-xs">{data.note}</p>
              </>
            ) : (
              <>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-7xl font-black tabular-nums" data-testid="badge-score">{data.score}</span>
                  <span className="opacity-80 text-lg font-semibold">/100</span>
                </div>
                <div className="mt-3 text-xl font-bold" data-testid="badge-tier">{data.tier}</div>
                <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 bg-white/15 backdrop-blur rounded-full text-xs uppercase tracking-[0.15em] font-semibold">
                  <Shield className="w-3.5 h-3.5" /> Independently verified by Sonote
                </div>
              </>
            )}
          </div>
          <div className="p-10 bg-white">
            <h2 className="font-bold text-xl">How this is calculated</h2>
            <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
              The Clinic Wellness Index combines two anonymized signals from the last 30 days: how many
              sonographers actively participate in daily wellness check-ins, and the inverse of average
              reported pain. No personal health data is ever exposed.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex items-center gap-2"><Users className="w-4 h-4" style={{ color: "var(--gold)" }} /><span>Active sonographers: {data.total_staff}</span></li>
              {data.participation_pct !== null && <li className="flex items-center gap-2"><Shield className="w-4 h-4" style={{ color: "var(--gold)" }} /><span>Participation: {data.participation_pct}%</span></li>}
              <li className="flex items-center gap-2"><Calendar className="w-4 h-4" style={{ color: "var(--gold)" }} /><span>Window: {data.month}</span></li>
            </ul>
            <Link to="/features" className="btn-outline mt-8 inline-flex" data-testid="badge-learn-more">Learn more about Sonote</Link>
          </div>
        </section>

        <div className="mt-10 text-center text-sm text-zinc-500">
          Looking to improve your clinic&apos;s score? <Link to="/contact" className="font-semibold" style={{ color: "var(--gold-hover)" }}>Book a demo</Link>.
        </div>
      </main>
    </div>
  );
}
